import React, { useState } from "react";

import { InvoiceServiceOrderDto, ServiceOrderDto } from "services/serviceOrders/restModels";
import { formatDate } from "shared/lib/dateTime";
import { formatPrice } from "shared/lib/price";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { ConfirmDialog } from "shared/ui/ConfirmDialog";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";
import { EmptyState } from "shared/ui/EmptyState";
import { Skeleton } from "shared/ui/Skeleton";

import { MarkInvoicePaidDialog } from "../MarkInvoicePaidDialog";
import { OrderDocuments } from "../OrderDocuments";

import {
    SERVICE_ORDER_STATUS_LABELS,
    SERVICE_ORDER_STATUS_TONES,
    SERVICE_ORDER_TYPE_LABELS,
} from "../../constants";
import { InvoiceActions, MutedLine, StatusBadge, StatusStripe, TariffCell } from "../../styles";

interface PaymentsTabProps {
    orders: ServiceOrderDto[];
    loading: boolean;
    error: boolean;
    savingInvoiceId: string | null;
    canChangeInvoiceStatus: boolean;
    busyInvoiceId: string | null;
    onOpenInvoice: (invoice: InvoiceServiceOrderDto) => void;
    onOpenAct: (invoice: InvoiceServiceOrderDto) => void;
    onChangeInvoiceStatus: (
        invoiceId: string,
        status: "Paid" | "Cancelled",
        paidAt: number | null
    ) => void;
    onRetry: () => void;
}

interface PendingAction {
    invoiceId: string;
    status: "Paid" | "Cancelled";
}

const orderDate = (order: ServiceOrderDto) => formatDate(order.paidAt || order.createdAt);

export const PaymentsTab = ({
    orders,
    loading,
    error,
    savingInvoiceId,
    canChangeInvoiceStatus,
    busyInvoiceId,
    onOpenInvoice,
    onOpenAct,
    onChangeInvoiceStatus,
    onRetry,
}: PaymentsTabProps) => {
    const [pending, setPending] = useState<PendingAction | null>(null);

    if (error) {
        return (
            <Banner tone="danger" title="Не удалось загрузить историю оплат">
                <Button size="small" variant="outlined" color="neutral" onClick={onRetry}>
                    Повторить
                </Button>
            </Banner>
        );
    }

    if (loading) return <Skeleton height="240px" />;

    if (orders.length === 0) {
        return <EmptyState title="Оплат пока не было" />;
    }

    const columns: DataTableColumn<ServiceOrderDto>[] = [
        {
            key: "statusStripe",
            role: "indicator",
            render: (order) => (
                <StatusStripe
                    $tone={SERVICE_ORDER_STATUS_TONES[order.status as keyof typeof SERVICE_ORDER_STATUS_TONES]}
                />
            ),
        },
        {
            key: "tariff",
            title: "Тариф",
            role: "primary",
            width: 300,
            render: (order) => (
                <TariffCell>
                    <div>{`${order.tariffTitle} · ${order.periodTitle}`}</div>
                    <MutedLine>
                        {SERVICE_ORDER_TYPE_LABELS[order.type]}{" "}
                        {order.type === "Invoice" ? order.invoiceId : `#${order.orderId}`}
                    </MutedLine>
                </TariffCell>
            ),
        },
        {
            key: "status",
            title: "Статус",
            role: "secondary",
            grow: true,
            render: (order) => (
                <StatusBadge
                    $tone={SERVICE_ORDER_STATUS_TONES[order.status as keyof typeof SERVICE_ORDER_STATUS_TONES]}
                >
                    {SERVICE_ORDER_STATUS_LABELS[order.status as keyof typeof SERVICE_ORDER_STATUS_LABELS]}
                </StatusBadge>
            ),
        },
        {
            key: "price",
            title: "Сумма",
            role: "secondary",
            width: 110,
            render: (order) => formatPrice(order.price.price, order.price.currencyId),
        },
        {
            key: "date",
            title: "Дата",
            role: "secondary",
            width: 100,
            render: orderDate,
        },
        {
            key: "document",
            title: "Документы",
            role: "secondary",
            width: 110,
            render: (order) => (
                <OrderDocuments
                    order={order}
                    busy={order.type === "Invoice" && busyInvoiceId === order.invoiceId}
                    onOpenInvoice={onOpenInvoice}
                    onOpenAct={onOpenAct}
                />
            ),
        },
        {
            key: "actions",
            title: "Действия",
            role: "action",
            width: 130,
            render: (order) => {
                // Статус меняем только у счёта: оплату картой проводит эквайринг.
                if (order.type !== "Invoice" || order.status !== "New") return null;

                return (
                    <InvoiceActions>
                        <Button
                            size="small"
                            variant="outlined"
                            color="neutral"
                            disabled={savingInvoiceId === order.invoiceId}
                            onClick={() =>
                                setPending({ invoiceId: order.invoiceId, status: "Paid" })
                            }
                        >
                            Оплачен
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="danger"
                            disabled={savingInvoiceId === order.invoiceId}
                            onClick={() =>
                                setPending({ invoiceId: order.invoiceId, status: "Cancelled" })
                            }
                        >
                            Отменить
                        </Button>
                    </InvoiceActions>
                );
            },
        },
    ];

    return (
        <>
            <DataTable
                columns={canChangeInvoiceStatus ? columns : columns.filter(({ key }) => key !== "actions")}
                rows={orders}
                rowKey={(order) => (order.type === "Card" ? `card-${order.orderId}` : order.invoiceId)}
            />

            <MarkInvoicePaidDialog
                open={pending?.status === "Paid"}
                invoiceId={pending?.invoiceId ?? null}
                saving={savingInvoiceId !== null}
                onClose={() => setPending(null)}
                onConfirm={(paidAt) => {
                    if (!pending) return;

                    onChangeInvoiceStatus(pending.invoiceId, "Paid", paidAt);
                    setPending(null);
                }}
            />

            <ConfirmDialog
                open={pending?.status === "Cancelled"}
                title="Отменить счёт?"
                description={`Счёт № ${pending?.invoiceId} будет отменён.`}
                confirmLabel="Отменить счёт"
                tone="danger"
                saving={savingInvoiceId !== null}
                onClose={() => setPending(null)}
                onConfirm={() => {
                    if (!pending) return;

                    onChangeInvoiceStatus(pending.invoiceId, "Cancelled", null);
                    setPending(null);
                }}
            />
        </>
    );
};
